import {useState, useRef, useCallback, useMemo, useEffect} from 'react'
import {clsx} from 'clsx'
import {IconButton, Spinner, TreeView, useOnEscapePress} from '@primer/react'
import {SidebarCollapseIcon, SidebarExpandIcon, XIcon} from '@primer/octicons-react'
import {DiffFileHeader} from '@github-ui/diff-file-header'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import type {StaticDiffLine} from '@github-ui/diff-lines/conversation-types'
import {useTaskDiffs} from '../../hooks/use-task-diffs'
import {parseUnifiedDiff} from '../../utils/parse-unified-diff'
import {DiffFileTree} from '@github-ui/diff-file-tree/file-tree'
import type {FileProps} from '@github-ui/diff-file-tree/file-tree'
import type {DiffDelta} from '@github-ui/diff-file-tree/diff-file-tree-helpers'
import {FileStatusIcon} from '@github-ui/diff-file-tree/file-status-icon'
import type {DiffData} from '../../types/diff'
import {blobPath} from '@github-ui/paths'
import styles from './DiffOverlay.module.css'

const MOBILE_BREAKPOINT = 768

// Map our short status codes to PatchStatus strings used by the file tree
function mapStatus(status: string): string {
  switch (status) {
    case 'A':
      return 'ADDED'
    case 'D':
      return 'DELETED'
    case 'R':
      return 'RENAMED'
    case 'C':
      return 'COPIED'
    case 'M':
    default:
      return 'MODIFIED'
  }
}

function toDiffDeltas(files: DiffData['files']): DiffDelta[] {
  return files.map(file => ({
    path: file.path,
    pathDigest: file.path_digest,
    changeType: mapStatus(file.status),
  }))
}

interface DiffOverlayProps {
  isOpen: boolean
  onClose: () => void
  repoNwo: string
  taskId: string
  baseRef: string
  headRef: string
  initialFilePath?: string | null
}

export function DiffOverlay({isOpen, onClose, repoNwo, taskId, baseRef, headRef, initialFilePath}: DiffOverlayProps) {
  const {data: diffs, isLoading} = useTaskDiffs(repoNwo, taskId, baseRef, headRef, {
    includePatch: true,
    enabled: isOpen,
  })

  const overlayRef = useRef<HTMLDivElement>(null)

  useOnEscapePress(onClose, [onClose])
  const [isMobile, setIsMobile] = useState(false)
  const [fileTreeOverride, setFileTreeOverride] = useState<boolean | null>(null)

  // Sync isMobile with container width via ResizeObserver.
  // Also resets fileTreeOverride when crossing the breakpoint so the file tree
  // collapses/expands to its default state for the new size.
  useEffect(() => {
    const el = overlayRef.current
    if (!el) return
    let prevWidth: number | null = null
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return
      const width = entry.contentRect.width
      const nowMobile = width <= MOBILE_BREAKPOINT
      const wasMobile = prevWidth !== null && prevWidth <= MOBILE_BREAKPOINT
      if (prevWidth !== null && nowMobile !== wasMobile) {
        setFileTreeOverride(null)
      }
      prevWidth = width
      setIsMobile(nowMobile)
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [isOpen])

  const showFileTree = fileTreeOverride ?? !isMobile

  const diffPanelRef = useRef<HTMLDivElement>(null)

  const scrollToFile = useCallback(
    (filePath: string) => {
      const panel = diffPanelRef.current
      if (!panel) return
      const element = panel.querySelector<HTMLElement>(`[data-file-path="${CSS.escape(filePath)}"]`)
      if (element) {
        panel.scrollTo({top: element.offsetTop - panel.offsetTop, behavior: 'smooth'})
      }
    },
    [diffPanelRef],
  )

  const handleFileSelect = useCallback(
    (filePath: string) => {
      scrollToFile(filePath)
      if (isMobile) {
        setFileTreeOverride(false)
      }
    },
    [scrollToFile, isMobile],
  )

  const diffDeltas = diffs ? toDiffDeltas(diffs.files) : []

  const fileNodeRenderer = useCallback(
    ({file}: FileProps) => {
      return (
        <TreeView.Item id={file.diff.path} onSelect={() => handleFileSelect(file.filePath)} key={file.diff.pathDigest}>
          <TreeView.LeadingVisual>
            <FileStatusIcon status={file.diff.changeType} />
          </TreeView.LeadingVisual>
          {file.fileName}
        </TreeView.Item>
      )
    },
    [handleFileSelect],
  )

  // Scroll to initial file once diffs are loaded
  useEffect(() => {
    if (isOpen && initialFilePath && diffs && !isLoading) {
      scrollToFile(initialFilePath)
    }
  }, [isOpen, initialFilePath, diffs, isLoading, scrollToFile])

  if (!isOpen) {
    return null
  }

  return (
    <div className={styles.overlay} ref={overlayRef}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.headerTitle}>
            {diffs ? `${diffs.files.length} file${diffs.files.length !== 1 ? 's' : ''} changed` : 'Changed files'}
          </span>
        </div>
        <div className={styles.headerActions}>
          <IconButton
            icon={showFileTree ? SidebarCollapseIcon : SidebarExpandIcon}
            aria-label={showFileTree ? 'Collapse file tree' : 'Expand file tree'}
            variant="invisible"
            size="small"
            onClick={() => setFileTreeOverride(!showFileTree)}
            className={styles.headerButton}
          />
          <IconButton
            icon={XIcon}
            aria-label="Close diff overlay"
            variant="invisible"
            size="small"
            onClick={onClose}
            className={styles.headerButton}
          />
        </div>
      </div>
      <div className={styles.body}>
        {isLoading ? (
          <div className={styles.loading}>
            <Spinner size="medium" />
          </div>
        ) : !diffs || diffs.files.length === 0 ? (
          <div className={styles.emptyState}>No changes found.</div>
        ) : (
          <>
            <div className={styles.diffPanel} ref={diffPanelRef}>
              {diffs.files.map(file => (
                <DiffFileEntry key={file.path} file={file} repoNwo={repoNwo} headRef={headRef} />
              ))}
            </div>
            {showFileTree && (
              <div
                className={clsx(
                  styles.fileTreePanel,
                  isMobile && fileTreeOverride === true && styles.fileTreePanelMobileOpen,
                )}
              >
                <div className={styles.fileTreePanelHeader}>
                  <span className={styles.fileTreePanelTitle}>Files</span>
                  <IconButton
                    icon={XIcon}
                    aria-label="Close file tree"
                    variant="invisible"
                    size="small"
                    onClick={() => setFileTreeOverride(false)}
                    className={styles.headerButton}
                  />
                </div>
                <DiffFileTree diffs={diffDeltas} fileNodeRenderer={fileNodeRenderer} sortByDiffOrder />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

interface DiffFileEntryProps {
  file: DiffData['files'][number]
  repoNwo: string
  headRef: string
}

function DiffFileEntry({file, repoNwo, headRef}: DiffFileEntryProps) {
  const [isExpanded, setIsExpanded] = useState(true)
  const [owner, repo] = repoNwo.split('/', 2)
  const fileLinkHref = owner && repo ? blobPath({owner, repo, commitish: headRef, filePath: file.path}) : undefined
  const diffLines = useMemo(() => {
    if (file.lines) {
      return file.lines.map(line => ({
        type: line.type.toUpperCase() as StaticDiffLine['type'],
        html: line.html,
        text: line.text,
        left: line.left,
        right: line.right,
      }))
    }
    return file.patch ? parseUnifiedDiff(file.patch) : []
  }, [file.lines, file.patch])

  return (
    <div data-file-path={file.path} id={file.path}>
      <DiffFileHeader
        isCollapsed={!isExpanded}
        onToggleFileCollapsed={() => setIsExpanded(v => !v)}
        fileLinkHref={fileLinkHref}
        path={file.path}
        newPath={file.path}
        patchStatus={mapStatus(file.status)}
        linesAdded={file.additions}
        linesDeleted={file.deletions}
        linesChanged={file.changes}
      />
      {isExpanded && (
        <div className={styles.diffContainer}>
          {diffLines.length > 0 ? (
            <StaticUnifiedDiffPreview diffLines={diffLines} tabSize={4} className={styles.diffPreview} />
          ) : (
            <div className={styles.emptyState}>Binary file or no patch available.</div>
          )}
        </div>
      )}
    </div>
  )
}
